import { Observable, Subscription, of, combineLatest, iif } from 'rxjs';
import { WindowService } from '@services/window.service';
import {
  Component,
  OnDestroy,
  ViewChild,
  ViewChildren,
  OnInit,
  QueryList,
} from '@angular/core';
import { ConfigurationService } from '@services/configuration.service';
import {
  SubscriptionManager,
  GlobalFooterDisclaimer,
} from '@zelis/platform-ui-components';
import { GlobalHeaderAutosuggestComponent } from '../global-header/autosuggest/global-header-autosuggest.component';
import { TermAutosuggest } from '@classes/term-autosuggest.class';
import { Breakpoints } from '@classes/breakpoints.class';
import { SearchOverlay } from '@classes/search-overlay.class';
import { CcssService } from '@services/ccss/ccss.service';
import { AuthService } from '@services/auth.service';
import { PharmacyService } from '@services/pharmacy/pharmacy.service';
import { PharmacyDetails } from '@interfaces/pharmacy-details.model';
import { TelehealthService } from '@services/telehealth/telehealth.service';
import { MembersService } from '@services/members.service';
import { MleService } from '@services/mle.service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { trigger, transition, useAnimation } from '@angular/animations';
import { fadeIn } from 'ng-animate';
import {
  filter,
  first,
  map,
  switchMap,
  catchError,
  distinctUntilChanged,
  take,
} from 'rxjs/operators';
import { SettingsService } from '@services/settings.service';
import { FeaturesService } from '@services/features/features.service';
import { UserPreferencesService } from '../../services/user.preferences.service';
import { MemberParams } from '@interfaces/member-params.model';
import { PreferredAddress } from '@interfaces/preferred-address.model';
import { Member } from '../members';
import { HierarchyListComponent } from '../hierarchy-list/hierarchy-list.component';
import { ProceduresAwarenessOverlay } from '@classes/procedures-awareness-overlay.class';
import { ProceduresAwarenessOverlayComponent } from './procedures-awareness-overlay';
import { RtsWelcomeOverlay } from '@classes/rts-welcome-overlay.class';
import { RtsWelcomeOverlayComponent } from '../right-to-shop/rts-welcome-overlay';
import { GatedEntryService } from '@services/gated-entry/gated-entry.service';
import { DefaultMember } from '@interfaces/default-member.model';
import { MleStatus } from '@interfaces/mle-status.model';
import { NetworkSelectionDropdownService } from '@services/network-selection-dropdown/network-selection-dropdown.service';
import { GatedEntryStatus } from '@interfaces/gated-entry-status.interface';
import { IncentivizedProcedure } from '@interfaces/incentivized-procedure.model';
import { ProceduresService } from '@services/procedures.service';
import { RouteUtilities } from '@utilities/route.utilities';
import { StringParseUtilities } from '@utilities/string-parse.utilities';
import { FixedTooltipComponent } from '../fixed-tooltip/fixed-tooltip.component';
import {
  AncillaryNetwork,
  AncillaryAllowed,
  AncillaryLinkItem,
} from '@interfaces/ancillary-network.model';
import { AuthStatus } from '@interfaces/auth-status.model';
import { HomeModuleSortOrder } from '@interfaces/home-module-sort-order.model';
import { Store, select } from '@ngrx/store';
import { AppState } from '@state/app.state';
import { AuthStoreSelectors } from '@store/auth';
import { GuidedSearchService } from '@services/guided-search/guided-search.service';
import { HasHistoricalClaimsConfig } from '@interfaces/has-historical-claims.model';
import { ActivatedRoute, Router } from '@angular/router';
import { clone, isEqual } from 'lodash';
import { selectSettingsFilters } from '@store/search-filters/settings-filters/settings-filters.selectors';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(
        '* => *',
        useAnimation(fadeIn, {
          params: { timing: 1.1 },
        })
      ),
    ]),
  ],
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('autosuggest') autosuggest: GlobalHeaderAutosuggestComponent;
  @ViewChildren(FixedTooltipComponent)
  networkToutTooltips: QueryList<FixedTooltipComponent>;

  public auth: AuthStatus;
  public subscriptions = new SubscriptionManager();
  public incentivesEnabled = false;
  public hsaEnabled = false;
  public rxEnabled = false;
  public telehealthSsoEnabled: boolean;
  public homeBackgroundColor: Observable<string> =
    this.ccssService.listenToClass('home_background_color');
  public benefitsEnabled = false;
  public costEnabled: boolean;
  public closeEmailTray: boolean = false;
  public member: Member;
  public emailSaveSuccess: boolean;
  public emailCaptureEnabled: boolean = false;
  public suppressEmailTray: boolean = false;
  public showCustomerServiceLayout: boolean = false;
  public disclaimers: GlobalFooterDisclaimer[];
  public networkSelectionDropdownEnabled: Observable<boolean>;
  public rtsProcedures: IncentivizedProcedure[];
  public isInitialPageLoad: boolean;
  public showUuxSearchAgainTooltip: boolean;
  public ancillaryNetworks: AncillaryLinkItem[] = [];
  public ancillaryAllowed: AncillaryAllowed = {
    authAllowed: true,
    unAuthAllowed: true,
  };
  public displayAncillaryLinks: boolean = false;
  public moduleSortOrder: HomeModuleSortOrder;
  public rx_savings_eligible: boolean = false;
  public mleEligible: boolean;
  public guidedSearchReady: boolean = false;
  public displayBillingSearch: boolean;
  private hasBannerShown: boolean;
  private guidedSearchEnabled: boolean;

  constructor(
    public termAutosuggest: TermAutosuggest,
    public breakpoints: Breakpoints,
    public searchOverlay: SearchOverlay,
    public authService: AuthService,
    public ccssService: CcssService,
    public membersService: MembersService,
    public pharmacyService: PharmacyService,
    public telehealthService: TelehealthService,
    public mleService: MleService,
    public networkSelectionDropdownService: NetworkSelectionDropdownService,
    private dialog: MatDialog,
    private configService: ConfigurationService,
    private settingsService: SettingsService,
    private featuresService: FeaturesService,
    private userPreferencesService: UserPreferencesService,
    private bottomSheet: MatBottomSheet,
    private proceduresAwarenessOverlay: ProceduresAwarenessOverlay,
    private rtsWelcomeOverlay: RtsWelcomeOverlay,
    private windowService: WindowService,
    private gatedEntryService: GatedEntryService,
    private proceduresService: ProceduresService,
    private routeUtilities: RouteUtilities,
    private stringParseUtilities: StringParseUtilities,
    private store: Store<AppState>,
    private guidedSearchService: GuidedSearchService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    this.cleanUrl();
    this.subscriptions.add(this.subscribeToGuidedSearchReady());
    this.subscriptions.add(this.subscribeToNetworkTooltip());
    this.subscriptions.add(this.subscribeToFeatures());
    this.subscriptions.add(this.subscribeToAuth());
    this.subscriptions.add(this.getMember());
    this.subscriptions.add(this.getPharmacy());
    this.subscriptions.add(this.subscribeToEmailCapture());
    this.subscriptions.add(this.subscribeToCustServInfoLayout());
    this.suppressEmailTray = this.emailTraySuppressed();
    this.gatedEntryStatus();
    this.hasBannerShown = this.windowService['sessionStorage'].getItem(
      'proceduresAwarenessBannerShown'
    );
    this.subscribeToMleStatus();
    this.fetchDisclaimers();
    this.windowService['sessionStorage'].removeItem('searchFilters');
    this.networkSelectionDropdownEnabled =
      this.networkSelectionDropdownService.getNetworkSelectionDropdownConfig();
    this.guidedSearchEnabled =
      !!this.routeUtilities.getParamFromUrl('guided_search');
    this.getRtsProcedures();
    this.getTelehealthEnabled();
    this.subscribeToAncillaryNetworks();
    this.subscribeToModuleSortOrder();
    this.subscriptions.add(this.showBillingSearchLink());
    this.showUuxSearchAgainToolTipIfEnabled();
    this.isInitialPageLoad =
      this.networkSelectionDropdownService.getInitialPageLoadState;
  }

  ngOnDestroy(): void {
    this.networkSelectionDropdownService.setInitialPageLoadState = false;
    this.networkSelectionDropdownService.updateNetworkChangeTooltip(false);
    this.subscriptions.destroy();
  }

  public showIncentivesFeature(): boolean {
    return !!(this.incentivesEnabled && this.mleEligible);
  }

  public showPromotedProcedures(): boolean {
    return this.mleEligible && this.costEnabled && !this.incentivesEnabled;
  }

  public showEmailTray(): boolean {
    return (
      !this.closeEmailTray &&
      !this.suppressEmailTray &&
      this.auth.auth_status &&
      this.incentivesEnabled &&
      this.emailCaptureEnabled &&
      !this.member.email_address
    );
  }

  public emailTraySuppressed(): boolean {
    const startDateData = this.stringParseUtilities.jsonParse(
      localStorage.getItem('suppressEmailTrayStartDate')
    );
    const today = new Date();
    const startDate = new Date(startDateData);
    const endDate = new Date(startDate.setDate(startDate.getDate() + 30));
    return startDateData && today <= endDate;
  }

  public saveEmail(email: PreferredAddress): void {
    const duration = 4000;
    this.subscriptions.add(
      this.userPreferencesService
        .updatePreferredAddress(email, new MemberParams(this.member.primary))
        .subscribe(
          (resp) => {
            this.emailSaveSuccess = true;
            setTimeout(() => {
              this.closeEmailTray = true;
            }, duration);
          },
          (err) => (this.emailSaveSuccess = false)
        )
    );
  }

  public onDismissSearchTooltipClicked(isForSession: boolean): void {
    isForSession &&
      this.windowService['sessionStorage'].setItem(
        'dismissUuxSearchAgain',
        true
      );
    this.showUuxSearchAgainTooltip = false;
  }

  public openNetworkTray(): void {
    const bottomRef = this.bottomSheet.open(HierarchyListComponent, {
      panelClass: 'full-screen-sheet',
      data: {
        type: 'network',
        title: 'app_global_network',
      },
    });
  }

  public triggerRtsWelcomeOverlay(): void {
    this.guidedSearchEnabled = false;
    if (this.rtsProcedures) {
      this.openRtsWelcomeOverlay();
    }
  }

  public showBillingSearchLink(): Subscription {
    return combineLatest([
      this.settingsService.getSetting(
        'has_historical_claims',
        HasHistoricalClaimsConfig
      ),
      this.store.pipe(select(AuthStoreSelectors.getAuthStatus)),
      this.mleService.status,
      this.settingsService.getSetting('suppress_search_by_billing_link'),
    ]).subscribe(
      ([historicalClaimsConfig, auth, mleStatus, suppressSearchByBillingLink]: [
        any,
        AuthStatus,
        any,
        boolean
      ]) => {
        const hasHistoricalClaims = historicalClaimsConfig.enabled;
        const authStatus = auth.auth_status;
        const mleEligible = mleStatus && mleStatus.isEligible;
        switch (true) {
          case this.member?.future_member:
            this.displayBillingSearch = false;
            break;
          case !!hasHistoricalClaims &&
            !!authStatus &&
            !!mleEligible &&
            !suppressSearchByBillingLink:
            this.displayBillingSearch = true;
            break;
          case !hasHistoricalClaims:
          case !authStatus:
          case !mleEligible:
          case !!hasHistoricalClaims && !!suppressSearchByBillingLink:
            this.displayBillingSearch = false;
            break;
          default:
            this.displayBillingSearch = true;
        }
      }
    );
  }

  private subscribeToEmailCapture(): Subscription {
    return this.getEmailCaptureEnabledConfig().subscribe(
      (emailCapture) => (this.emailCaptureEnabled = emailCapture)
    );
  }

  private subscribeToAuth(): Subscription {
    return this.store
      .pipe(select(AuthStoreSelectors.getAuthStatus))
      .subscribe((data) => {
        this.auth = data;
        this.getDisplayAncillaryLinks();
      });
  }

  private subscribeToAncillaryNetworks(): void {
    this.subscriptions.add(
      this.settingsService
        .getSetting('ancillary_links')
        .subscribe((networks: AncillaryNetwork) => {
          const defaultAllowed = {
            authAllowed: true,
            unAuthAllowed: true,
          };
          this.ancillaryNetworks = networks?.links || [];
          this.ancillaryAllowed = networks?.allowed || defaultAllowed;
          this.getDisplayAncillaryLinks();
        })
    );
  }

  private getDisplayAncillaryLinks(): void {
    this.displayAncillaryLinks =
      ((!this.auth.auth_status && this.ancillaryAllowed.unAuthAllowed) ||
        (this.auth.auth_status && this.ancillaryAllowed.authAllowed)) &&
      this.ancillaryNetworks.length > 0;
  }

  private subscribeToNetworkTooltip(): Subscription {
    return this.networkSelectionDropdownService.networkChangeTooltip.subscribe(
      (showValue: boolean) => {
        const dismissUuxSearchAgain: string = this.windowService[
          'sessionStorage'
        ].getItem('dismissUuxSearchAgain');
        if (
          showValue &&
          (!dismissUuxSearchAgain || dismissUuxSearchAgain === 'false')
        ) {
          this.showUuxSearchAgainTooltip = showValue;
          this.windowService['sessionStorage'].setItem(
            'dismissUuxSearchAgain',
            false
          );
        }
      }
    );
  }

  private subscribeToCustServInfoLayout(): Subscription {
    return this.settingsService
      .getSetting('customer_service_info_layer')
      .subscribe((val) => {
        this.showCustomerServiceLayout = val;
      });
  }

  private getMember(): Subscription {
    return this.membersService.member
      .pipe(first((member) => !!member))
      .subscribe((member) => {
        this.incentivesEnabled = member.incentives_enabled;
        this.hsaEnabled = member.hsa_balance_enabled;
        this.rx_savings_eligible = member.rx_savings_eligible;
        this.member = member;
      });
  }

  private getPharmacy(): Subscription {
    return this.pharmacyService
      .requestDetailsWhenAuthorized()
      .subscribe((pharmacy: PharmacyDetails) => {
        this.rxEnabled = pharmacy.enabled;
      });
  }

  private getEmailCaptureEnabledConfig(): Observable<boolean> {
    return this.configService.signatureAppParamsResolved.pipe(
      filter((sig) => !!sig),
      first((sig) => !!sig),
      switchMap((sig) => {
        return this.settingsService
          .requestSetting(sig, 'email_capture')
          .pipe(catchError(() => of({ settings: [{}] })));
      }),
      map((config) => {
        return this.emailEnabledData(config);
      })
    );
  }

  private emailEnabledData(config): boolean {
    if (
      config.settings[0] &&
      config.settings[0].email_capture &&
      config.settings[0].email_capture.enabled
    ) {
      return !!config.settings[0].email_capture.enabled;
    }
    return false;
  }

  private subscribeToFeatures(): Subscription {
    return this.featuresService.getFeatureFlags().subscribe((features) => {
      this.costEnabled = features?.cost;
    });
  }

  private gatedEntryStatus(): void {
    const sub = combineLatest([
      this.gatedEntryService.status,
      this.membersService.defaultMember,
      this.settingsService.getSetting('public_cost_overlay_enabled'),
    ]).subscribe(
      ([gatedStatus, member, publicCostOverlayEnabled]: [
        GatedEntryStatus,
        DefaultMember,
        boolean
      ]) => {
        if (
          this.shouldOpenProceduresAwarenessOverlay(
            gatedStatus,
            member,
            publicCostOverlayEnabled
          )
        ) {
          this.proceduresAwarenessOverlay.open(
            ProceduresAwarenessOverlayComponent
          );
          this.windowService['sessionStorage'].setItem(
            'proceduresAwarenessBannerShown',
            true
          );
        }
      }
    );
    this.subscriptions.add(sub);
  }

  private shouldOpenProceduresAwarenessOverlay(
    status: GatedEntryStatus,
    member: DefaultMember,
    publicCostOverlayEnabled: boolean
  ) {
    return (
      !status.isOpened &&
      !this.auth.auth_status &&
      !this.hasBannerShown &&
      !!member &&
      member.inAllowedLocation &&
      publicCostOverlayEnabled
    );
  }

  private subscribeToMleStatus(): void {
    this.subscriptions.add(
      this.mleService.status.subscribe((mleStatus: MleStatus) => {
        this.mleEligible = mleStatus && mleStatus.isEligible;
      })
    );
  }

  private fetchDisclaimers(): void {
    this.subscriptions.add(
      this.settingsService
        .getSetting('disclaimers')
        .pipe(map((settings) => settings && settings['homepage']))
        .subscribe((disclaimers) => (this.disclaimers = disclaimers))
    );
  }

  private getRtsProcedures(): void {
    this.subscriptions.add(
      this.proceduresService
        .requestIncentivizedProcedures(true)
        .pipe(
          distinctUntilChanged((prev, curr) => prev?.length === curr?.length),
          filter((procedures) => procedures && procedures.length > 0)
        )
        .subscribe((procedures: IncentivizedProcedure[]) => {
          this.openRtsWelcomeOverlay();
          this.rtsProcedures = procedures;
          this.rtsProcedures.sort((a, b) =>
            a.locale_name.localeCompare(b.locale_name)
          );
        })
    );
  }

  private openRtsWelcomeOverlay(): void {
    if (this.guidedSearchEnabled) {
      return;
    }
    const rtsWelcomeOverlayData = this.stringParseUtilities.jsonParse(
      localStorage.getItem('rtsWelcomeOverlayData')
    );
    const resetDate = rtsWelcomeOverlayData && rtsWelcomeOverlayData.resetDate;
    const today = new Date();
    if (!(resetDate && today <= new Date(resetDate))) {
      this.rtsWelcomeOverlay.open(
        RtsWelcomeOverlayComponent,
        this.breakpoints.isMobile
      );
      const newResetDate = new Date(today.setDate(today.getDate() + 30));
      localStorage.setItem(
        'rtsWelcomeOverlayData',
        JSON.stringify({ resetDate: newResetDate })
      );
      return;
    }

    this.openTranslatedRtsWelcomeOverlay(rtsWelcomeOverlayData);
  }

  private openTranslatedRtsWelcomeOverlay(rtsWelcomeOverlayData: any): void {
    const language = this.routeUtilities.getParamFromUrl('locale');
    const translatedOverlayOpened = rtsWelcomeOverlayData[language];
    if (language !== 'en' && !translatedOverlayOpened) {
      this.rtsWelcomeOverlay.open(
        RtsWelcomeOverlayComponent,
        this.breakpoints.isMobile
      );
      rtsWelcomeOverlayData[language] = true;
      localStorage.setItem(
        'rtsWelcomeOverlayData',
        JSON.stringify(rtsWelcomeOverlayData)
      );
    }
  }

  private getTelehealthEnabled(): void {
    this.subscriptions.add(
      this.telehealthService.vendors.subscribe((vendors) => {
        this.telehealthSsoEnabled = vendors && vendors.length > 0;
      })
    );
  }

  private getModuleSortOrder(): Observable<HomeModuleSortOrder> {
    return this.store.pipe(select(AuthStoreSelectors.getAuthStatus)).pipe(
      filter((auth) => auth.resolved),
      switchMap((auth) =>
        iif(
          () => auth.auth_status,
          this.settingsService.getSetting(
            'home_module_sort_auth',
            HomeModuleSortOrder
          ),
          this.settingsService.getSetting(
            'home_module_sort_unauth',
            HomeModuleSortOrder
          )
        )
      )
    );
  }

  private subscribeToModuleSortOrder(): void {
    this.subscriptions.add(
      this.getModuleSortOrder().subscribe(
        (sortOrder) => (this.moduleSortOrder = sortOrder)
      )
    );
  }

  private subscribeToGuidedSearchReady(): Subscription {
    return this.guidedSearchService.isReady.pipe(take(1)).subscribe(() => {
      this.guidedSearchReady = true;
    });
  }

  private cleanUrl(): void {
    combineLatest([
      this.route.queryParams,
      this.store.pipe(select(selectSettingsFilters), take(1)),
    ]).subscribe(([params, filterSettings]) => {
      const updateParams = clone(params);
      this.routeUtilities.removeSerpQueryParams(
        updateParams,
        filterSettings.filters
      );
      if (!isEqual(updateParams, params)) {
        this.router.navigate([], {
          queryParams: updateParams,
        });
      }
    });
  }

  private showUuxSearchAgainToolTipIfEnabled() {
    this.networkSelectionDropdownEnabled.subscribe(
      (networkSelectionDropdownEnabled: boolean) => {
        if (networkSelectionDropdownEnabled) {
          this.networkSelectionDropdownService.updateNetworkChangeTooltip(true);
          this.windowService['sessionStorage'].removeItem('uuxLocale');
        }
      }
    );
  }
}
